import { apiClient } from './apiClient';

/**
 * A whitelabel tenant's own telephony and speech credentials.
 *
 * Secrets ARE returned decrypted by the read endpoint (super-admin only), so the
 * UI can show a saved value pre-filled — matching the agent-level Voice Config
 * tab. Saving sends the field's current value as-is: a blank value clears the
 * stored secret, same as any other field here.
 */

/** Carrier code. Registry-driven on the server (twilio, telnyx, plivo, …), so not a closed union. */
export type TelephonyProvider = string;
export type TtsProvider = 'twilio' | 'elevenlabs' | 'supertonic' | 'fish' | 'sarvam';
export type SttProvider = 'deepgram' | 'sarvam';

export interface TenantVoiceConfig {
  telephonyProvider: TelephonyProvider;
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioPhoneNumber: string;
  telnyxApiKey: string;
  telnyxPhoneNumber: string;
  ttsProvider: TtsProvider;
  ttsApiKey: string;
  ttsBaseUrl: string;
  ttsVoiceId: string;
  ttsModelId: string;
  sttProvider: SttProvider;
  sttApiKey: string;
  sttModel: string;
  language: string;
  keyterms: string;
  idleTimeoutSeconds: number;
  idleMaxPrompts: number;
  enableHoldMusic: boolean;
  enableAmbientMusic: boolean;
  ambientMusicKey: string;
  ambientMusicBucket: string;
  hasTwilioAuthToken: boolean;
  hasTelnyxApiKey: boolean;
  hasTtsApiKey: boolean;
  hasSttApiKey: boolean;
  /** Providers with declared fields (Plivo, …): plain values, and only WHETHER each secret is set —
   *  their secrets are never sent to the browser. */
  telephonyCredentials?: Record<string, { values: Record<string, string>; secretsSet: Record<string, boolean> }>;
}

/** A credential a telephony provider declares it needs (rendered generically by the form). */
export interface TelephonyProviderField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  placeholder?: string;
  help?: string;
}

export interface TelephonyProviderDescriptor {
  code: string;
  label: string;
  /** 'legacy' = Twilio/Telnyx dedicated fields; 'generic' = render `fields`. */
  credentialStorage: 'legacy' | 'generic';
  fields: TelephonyProviderField[];
  capabilities: { outbound: boolean; warmTransfer: boolean; signedWebhooks: boolean };
}

export interface VoiceConfigResponse {
  tenantId: string;
  isWhitelabeled: boolean;
  /** What this build actually implements, so the UI cannot drift from it. */
  providers: { telephony: string[]; tts: string[]; stt: string[] };
  /** What each telephony provider needs and supports, straight from the server's registry. */
  telephonyProviders?: TelephonyProviderDescriptor[];
  config: TenantVoiceConfig;
}

/**
 * What each TTS provider needs.
 *
 * They differ meaningfully: Twilio's built-in voices reuse the telephony
 * credentials, ElevenLabs is key-based, Supertonic is self-hosted so it wants a
 * URL and no key, and Fish Audio wants both. Driving the form from this keeps
 * irrelevant fields off the screen instead of showing an API key box for a
 * provider that has no API key.
 */
export const TTS_PROVIDER_SPEC: Record<TtsProvider, {
  label: string;
  needsApiKey: boolean;
  needsBaseUrl: boolean;
  voiceLabel: string;
  voicePlaceholder: string;
  modelPlaceholder: string;
  note?: string;
}> = {
  twilio: {
    label: 'Twilio (built-in)',
    needsApiKey: false,
    needsBaseUrl: false,
    voiceLabel: 'Voice',
    voicePlaceholder: 'Polly.Joanna-Generative',
    modelPlaceholder: '',
    note: 'Uses the telephony credentials above — no separate key required.',
  },
  elevenlabs: {
    label: 'ElevenLabs',
    needsApiKey: true,
    needsBaseUrl: false,
    voiceLabel: 'Voice ID',
    voicePlaceholder: '21m00Tcm4TlvDq8ikWAM',
    modelPlaceholder: 'eleven_turbo_v2',
  },
  supertonic: {
    label: 'Supertonic (self-hosted)',
    needsApiKey: false,
    needsBaseUrl: true,
    voiceLabel: 'Voice',
    voicePlaceholder: 'F1',
    modelPlaceholder: '',
    note: 'Self-hosted, so it is addressed by URL rather than an API key.',
  },
  fish: {
    label: 'Fish Audio',
    needsApiKey: true,
    needsBaseUrl: true,
    voiceLabel: 'Reference ID',
    voicePlaceholder: 'blank = account default voice',
    modelPlaceholder: 's1',
  },
  sarvam: {
    label: 'Sarvam AI (Indian languages, Hindi + English)',
    needsApiKey: true,
    needsBaseUrl: false,
    voiceLabel: 'Speaker',
    voicePlaceholder: 'simran_enhi_customer',
    modelPlaceholder: 'bulbul:v4-flash',
    note: 'Leave the API key blank to use the Sarvam key from Speech to text. Speaks English and Indian languages; calls in other languages use ElevenLabs.',
  },
};

/** Labels for the two legacy carriers, used only if the server's provider list is unavailable. */
export const TELEPHONY_PROVIDER_SPEC: Record<string, { label: string }> = {
  twilio: { label: 'Twilio' },
  telnyx: { label: 'Telnyx' },
};

export interface CallLanguageOption {
  code: string;
  label: string;
}

const auth = { authenticate: true };

export const voiceConfigService = {
  /** Carriers this build supports (no credentials) — for pickers outside the super-admin screen. */
  listTelephonyProviders() {
    return apiClient.get<{ providers: Array<Omit<TelephonyProviderDescriptor, 'fields'>> }>(`/core/voice/telephony-providers`, auth);
  },

  get(tenantId: string) {
    return apiClient.get<VoiceConfigResponse>(`/core/tenants/${tenantId}/voice-config`, auth);
  },

  update(tenantId: string, data: Record<string, unknown>) {
    return apiClient.put<{ message: string }>(`/core/tenants/${tenantId}/voice-config`, data, auth);
  },

  /** Call languages the server supports (code + label) — see core-ms call-language.ts. */
  listCallLanguages() {
    return apiClient.get<{ languages: CallLanguageOption[]; defaultLanguage: string }>(`/core/voice/call-languages`, auth);
  },
};
